import React, { useState } from 'react';
import { 
  Box, 
  Card, 
  CardMedia, 
  Container, 
  Typography, 
  Grid, 
  Button, 
  IconButton, 
  Avatar, 
  Tabs, 
  Tab, 
  TextField,
  Chip,
  Paper,
  Stack,
  Link,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  FormControl,
  InputLabel,
  FormHelperText
} from '@mui/material';
import { 
  Heart, 
  Edit, 
  MoreHorizontal, 
  MessageSquare, 
  Search, 
  ExternalLink, 
  Copy, 
  Zap,
  Share2,
  Download,
  Flag
} from 'lucide-react';

// タブパネルのコンポーネント
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// キャラクターカードコンポーネント
function CharacterCard({ name, subName, icon }) {
  const [hover, setHover] = useState(false);
  
  return (
    <Box sx={{ position: 'relative' }}>
      <Paper
        variant="outlined"
        sx={{ 
          p: 1.5, 
          display: 'flex', 
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': {
            bgcolor: 'rgba(0, 0, 0, 0.04)'
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Avatar src={icon || "/api/placeholder/40/40"} sx={{ width: 36, height: 36, mr: 1.5 }} />
        <Box>
          <Typography variant="body2">{name}</Typography>
          {subName && (
            <Typography variant="caption" color="text.secondary">{subName}</Typography>
          )}
        </Box>
      </Paper>
      
      {hover && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: -100,
            left: '50%',
            transform: 'translateX(-50%)',
            p: 2,
            zIndex: 100,
            minWidth: 220,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Avatar 
            src={icon || "/api/placeholder/40/40"}
            sx={{ width: 64, height: 64, mb: 1 }}
          />
          <Typography variant="body1" fontWeight="medium" textAlign="center">
            {name}
          </Typography>
          {subName && (
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {subName}
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
}

// プロンプト情報表示コンポーネント
function PromptSection({ label, content }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="caption" color="text.secondary" gutterBottom>{label}</Typography>
      <Paper variant="outlined" sx={{ p: 1 }}>
        <Typography variant="body2">{content}</Typography>
      </Paper>
    </Box>
  );
}

// パラメータ情報表示コンポーネント
function ParamItem({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" gutterBottom>{label}</Typography>
      <Paper variant="outlined" sx={{ p: 1 }}>
        <Typography variant="body2">{value}</Typography>
      </Paper>
    </Box>
  );
}

export default function MonsterBallPost() {
  const [tabValue, setTabValue] = useState(0);
  const [liked, setLiked] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const menuOpen = Boolean(menuAnchorEl);
  
  // 問題の選択肢
  const options = [
    {label:"過度な性的表現が含まれている(性表現を含む作品が全年齢として公開されているなど)",div:"1"},
    {label:"プライバシーまたは肖像権を侵害している",div:"2"},
    {label:"無断転載している",div:"3"},
    {label:"商業用の広告や宣伝、勧誘を目的とする情報が含まれている",div:"4"},
    {label:"過度なグロテスク表現が含まれている",div:"5"},
    {label:"児童ポルノまたは児童虐待に相当する表現が含まれている",div:"6"},
    {label:"その他、利用規約やガイドラインに違反している",div:"7"},
  ];
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };
  
  const handleReportDialogOpen = () => {
    setReportDialogOpen(true);
    handleMenuClose();
  };
  
  const handleReportDialogClose = () => {
    setReportDialogOpen(false);
  };
  
  const handleReportSubmit = () => {
    // ここで報告データを送信する処理を実装
    console.log('報告内容:', { reportReason, reportDetails });
    handleReportDialogClose();
    // 送信後に状態をリセット
    setReportReason('');
    setReportDetails('');
  };
  
  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Card sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        overflow: 'hidden',
        flex: 1,
        borderRadius: 0,
        boxShadow: 'none'
      }}>
        {/* 左側: 画像部分 */}
        <Box sx={{ 
          position: 'relative', 
          width: { xs: '100%', md: '60%' }, 
          bgcolor: 'black',
          height: { xs: '40vh', md: '100vh' }
        }}>
          <CardMedia
            component="img"
            image="/api/placeholder/800/500"
            alt="Monster Ball"
            sx={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              opacity: 0.6
            }}
          />
          <Typography
            variant="h2"
            component="h1"
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              fontWeight: 'bold',
              textAlign: 'center',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
            }}
          >
            Monster Ball
          </Typography>
        </Box>
        
        {/* 右側: コンテンツ部分 */}
        <Box sx={{ 
          width: { xs: '100%', md: '40%' }, 
          p: 2,
          height: { xs: '60vh', md: '100vh' },
          overflow: 'auto'
        }}>
          {/* 投稿情報ヘッダー */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              2024-12-28 12:14:55 投稿 • 閲覧 2 • いいね 0
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton 
                size="small" 
                onClick={() => setLiked(!liked)}
                color={liked ? "warning" : "default"}
              >
                <Heart size={16} fill={liked ? "currentColor" : "none"} />
              </IconButton>
              <Chip size="small" label="イラスト" sx={{ ml: 1 }} />
            </Box>
          </Box>
          
          {/* 編集ボタン */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<Edit size={16} />}
              size="small"
            >
              編集
            </Button>
            <IconButton 
              size="small"
              onClick={handleMenuOpen}
              aria-controls={menuOpen ? "post-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? "true" : undefined}
            >
              <MoreHorizontal size={20} />
            </IconButton>
            <Menu
              id="post-menu"
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'more-button',
              }}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleMenuClose}>
                <ListItemIcon>
                  <Share2 size={18} />
                </ListItemIcon>
                <ListItemText>共有</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMenuClose}>
                <ListItemIcon>
                  <Download size={18} />
                </ListItemIcon>
                <ListItemText>ダウンロード</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleReportDialogOpen}>
                <ListItemIcon>
                  <Flag size={18} />
                </ListItemIcon>
                <ListItemText>問題を報告</ListItemText>
              </MenuItem>
            </Menu>
            
            {/* 問題報告ダイアログ */}
            <Dialog
              open={reportDialogOpen}
              onClose={handleReportDialogClose}
              aria-labelledby="report-dialog-title"
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle id="report-dialog-title">問題を報告</DialogTitle>
              <DialogContent>
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel htmlFor="report-reason">選択</InputLabel>
                  <Select
                    native
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    label="選択"
                    inputProps={{
                      name: 'report-reason',
                      id: 'report-reason',
                    }}
                  >
                    <option aria-label="None" value="" />
                    {options.map(option => (
                      <option key={option.div} value={option.div}>{option.label}</option>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  margin="normal"
                  fullWidth
                  multiline
                  rows={4}
                  label="詳細"
                  placeholder="問題の詳細を入力してください"
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  variant="outlined"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  FormHelperTextProps={{
                    sx: { position: 'absolute', right: 0, top: '100%' }
                  }}
                />
                
                <Box sx={{ mt: 3, mb: 1 }}>
                  <TextField
                    fullWidth
                    label="Title"
                    variant="outlined"
                    value="ラニング"
                    disabled
                    size="small"
                    InputProps={{
                      sx: { bgcolor: '#f5f5f5' }
                    }}
                  />
                </Box>
              </DialogContent>
              <DialogActions>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleReportSubmit}
                  disabled={!reportReason || !reportDetails}
                  sx={{ 
                    bgcolor: '#1a73e8', 
                    '&:hover': { bgcolor: '#1557b0' },
                    '&.Mui-disabled': { bgcolor: '#ccc', color: '#666' } 
                  }}
                >
                  送信
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
          
          {/* 投稿者情報 */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar 
                sx={{ 
                  width: 40, 
                  height: 40, 
                  mr: 1,
                  background: 'linear-gradient(45deg, #8E2DE2, #4A00E0)'
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" fontWeight="medium">アワートAI 公式</Typography>
                  <Chip 
                    size="small" 
                    label="PRO" 
                    sx={{ ml: 0.5, height: 16, fontSize: '0.6rem', bgcolor: '#ff5722', color: 'white' }}
                  />
                  <Box 
                    sx={{ 
                      ml: 0.5, 
                      bgcolor: 'warning.main', 
                      width: 16, 
                      height: 16, 
                      borderRadius: '50%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}
                  >
                    <Box component="span" sx={{ color: 'white', fontSize: '0.7rem' }}>✓</Box>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">...</Typography>
              </Box>
              <IconButton size="small">
                <MoreHorizontal size={20} />
              </IconButton>
            </Box>
          </Paper>
          
          {/* メンバーシップボタン */}
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ mb: 2 }}
          >
            メンバーになって限定コンテンツを見る
          </Button>
          
          {/* タグ情報 */}
          <Box sx={{ mb: 2 }}>
            <Link href="#" underline="none" sx={{ mr: 1, color: 'primary.main' }}>
              #animaPencilXL_v500
            </Link>
            <Link href="#" underline="none" sx={{ color: 'primary.main' }}>
              #プロンプトあり
            </Link>
          </Box>
          
          {/* 関連キャラクター */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight="medium" color="text.primary" gutterBottom>
              関連キャラクター
            </Typography>
            <Stack spacing={1}>
              <CharacterCard 
                name="a" 
                icon="/api/placeholder/40/40"
              />
              <CharacterCard 
                name="紫月 黒葉" 
                subName="しづき くろは"
                icon="/api/placeholder/40/40"
              />
            </Stack>
          </Box>
          
          {/* タブメニュー */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="コンテンツタブ">
              <Tab label="コメント一覧" />
              <Tab label="情報" />
              <Tab label="その他の投稿" />
            </Tabs>
          </Box>
          
          {/* コメント一覧タブ */}
          <TabPanel value={tabValue} index={0}>
            <Alert 
              icon={<MessageSquare size={20} />}
              severity="info"
              action={
                <Button size="small" variant="outlined" color="info">
                  コメントする
                </Button>
              }
              sx={{ mb: 2 }}
            >
              1件目のコメントをしてみませんか？
            </Alert>
            
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                placeholder="コメントを記載"
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ mb: 1 }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <IconButton size="small" sx={{ bgcolor: '#f5f5f5' }}>
                  <MessageSquare size={16} />
                </IconButton>
                <Button 
                  variant="text" 
                  sx={{ bgcolor: '#f5f5f5', color: 'text.primary', px: 2 }}
                >
                  コメント
                </Button>
              </Box>
            </Paper>
          </TabPanel>
          
          {/* 情報タブ */}
          <TabPanel value={tabValue} index={1}>
            {/* ボタン行 */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<Search size={14} />}
                sx={{ textTransform: 'none' }}
              >
                類似検索
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={<Copy size={14} />}
                sx={{ textTransform: 'none' }}
              >
                プロンプト情報をコピー
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                color="warning"
                startIcon={<Zap size={14} />}
                sx={{ textTransform: 'none' }}
              >
                引用生成
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                color="primary"
                startIcon={<ExternalLink size={14} />}
                sx={{ textTransform: 'none' }}
              >
                リンク共有
              </Button>
            </Stack>
            
            {/* プロンプト情報 */}
            <PromptSection 
              label="プロンプト(出力したものの特徴を英語で入力)"
              content="(best quality,novel illustration,sketch),1girl, <lora:DynLighting:0.5>,long hair,blunt bangs,black eyes,black hair, (tall female)"
            />
            
            <PromptSection 
              label="ネガティブプロンプト(除外するものの特徴を英語で入力)"
              content="(worst quality, low quality:1.4),(monochrome:1.1),loli,"
            />
            
            {/* パラメータ情報 */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <ParamItem label="使用したモデル(直接記載)" value="animaPencilXL_v500" />
              </Grid>
              <Grid item xs={6} md={3}>
                <ParamItem label="Steps" value="20" />
              </Grid>
              <Grid item xs={6} md={3}>
                <ParamItem label="Scale" value="8" />
              </Grid>
              <Grid item xs={6} md={3}>
                <ParamItem label="Seed" value="1821244595" />
              </Grid>
            </Grid>
            
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6} md={4}>
                <ParamItem label="Sampler" value="DPM++ 2M Karras" />
              </Grid>
              <Grid item xs={6} md={4}>
                <ParamItem label="Strength" value="0.45" />
              </Grid>
              <Grid item xs={6} md={4}>
                <ParamItem label="Clip skip" value="2" />
              </Grid>
            </Grid>
            
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              <Grid item xs={6}>
                <ParamItem label="Hires Upscale" value="1.5" />
              </Grid>
              <Grid item xs={6}>
                <ParamItem label="Hires Upscaler" value="R-ESRGAN 4x+ Anime6B" />
              </Grid>
            </Grid>
            
            {/* Raw Info */}
            <PromptSection 
              label="Raw Info"
              content="(best quality,novel illustration,sketch),1girl, <lora:DynLighting:0.5>,long hair,blunt bangs,black eyes,black hair, (tall female)"
            />
          </TabPanel>
          
          {/* その他の投稿タブ */}
          <TabPanel value={tabValue} index={2}>
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              その他の関連投稿はまだありません
            </Box>
          </TabPanel>
        </Box>
      </Card>
    </Box>
  );
}